// Copyright 2025, University of Maryland, All Rights Reserved
import { axiosInstance, sanitizeResponseData } from './client';

export interface ComparisonSummary {
  comparison_id: string;
  status: string;
  created_at?: string;
  completed_at?: string;
  failed_at?: string;
  message?: string;
  result_size_mb?: string;
  has_inline_data: boolean;
  s3_key?: string;
  error?: string;
  documents_compared?: string[];
  comparison_type?: string;
  llm_cost?: string;
  similarity_score?: number;
  alignment_rating?: string;
  prompt_name?: string;
  analysis_type?: string;
  prompt_type?: string;
  created_by?: string;
}

export interface ComparisonListResponse {
  comparisons: ComparisonSummary[];
  count: number;
  org_id: string;
  ws_id: string;
  has_more: boolean;
  next_token?: string;
}

export interface ComparisonDetail {
  comparison_id: string;
  org_id: string;
  ws_id: string;
  status: string;
  created_at?: string;
  completed_at?: string;
  failed_at?: string;
  message?: string;
  error?: string;
  result_size_mb?: string;
  s3_key?: string;
  comparison_data?: any;
  data_source?: string;
  presigned_url?: string;
  presigned_url_expires_in?: string;
  s3_error?: string;
}

export const comparisonApi = {
  // Compare documents - supports both sync and async processing
  async compareDocuments(
    documentIds: string[],
    comparisonType: string,
    org_id: string,
    ws_id: string,
    user_id: string,
    prompt_type?: string,
    prompt_name?: string,
    analysis_type?: string,
    isBulk?: boolean,
    setA?: string[],
    setB?: string[],
    unifiedAnalysisMode?: boolean,
    llm_model?: string
  ): Promise<any> {
    try {
      console.log('Making API call to /analysis/documents/compare...');
      console.log('Bulk mode:', isBulk, 'Unified Analysis:', unifiedAnalysisMode, 'Set A:', setA, 'Set B:', setB);

      const params: Record<string, string> = {
        org_id,
        ws_id,
        user_id
      };

      // Add optional prompt parameters
      if (prompt_type) {
        params.prompt_type = prompt_type;
      }
      if (prompt_name) {
        params.prompt_name = prompt_name;
      }
      if (analysis_type) {
        params.analysis_type = analysis_type;
      }
      if (llm_model) {
        params.llm_model = llm_model;
      }

      // Build request body based on comparison mode
      const requestBody: any = {
        comparison_type: comparisonType
      };

      if (unifiedAnalysisMode && setA && setB) {
        // Unified analysis comparison mode - compare pre-analyzed content
        requestBody.comparison_mode = 'unified_analysis';
        requestBody.unified_analysis_mode = true;
        requestBody.set_a = setA;
        requestBody.set_b = setB;
        requestBody.document_ids = documentIds; // Send all IDs for tracking
      } else if (isBulk && setA && setB) {
        // Bulk comparison mode - send sets separately
        requestBody.comparison_mode = 'bulk';
        requestBody.set_a = setA;
        requestBody.set_b = setB;
        requestBody.document_ids = documentIds; // Still send all IDs for tracking
      } else {
        // Normal comparison mode
        requestBody.comparison_mode = 'normal';
        requestBody.document_ids = documentIds;
      }

      const response = await axiosInstance.post('/analysis/documents/compare', requestBody, {
        params,
        timeout: 60000, // 60 second timeout for comparisons
      });

      console.log('API call successful, processing response...');
      return sanitizeResponseData(response.data);
    } catch (error: any) {
      console.error('Compare documents error:', error);

      // Handle different types of errors
      if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        const errorData = error.response.data;

        console.error(`API Error ${status}:`, errorData);

        if (status === 500 && errorData?.detail?.includes('Float types are not supported')) {
          // This is our target error - backend succeeded but can't serialize response
          console.log('Detected float serialization error - backend processing likely succeeded');
          throw new Error('Document comparison completed successfully, but there was a response formatting issue. The backend needs to convert float values to Decimal or string before JSON serialization.');
        }

        throw new Error(errorData?.detail || `API Error ${status}`);
      } else if (error.request) {
        // Network error
        console.error('Network error:', error.request);
        throw new Error('Network error - please check your connection and try again.');
      } else {
        // Other error
        console.error('Unexpected error:', error.message);
        throw new Error(error.message || 'An unexpected error occurred.');
      }
    }
  },

  // Get comparison status for async processing
  async getComparisonStatus(comparisonId: string, org_id: string, ws_id: string): Promise<any> {
    try {
      const response = await axiosInstance.get(`/analysis/comparison-status/${comparisonId}`, {
        params: {
          org_id,
          ws_id
        },
        timeout: 30000, // 30 second timeout for status checks
      });

      return sanitizeResponseData(response.data);
    } catch (error: any) {
      console.error('Get comparison status error:', error);

      if (error.response?.status === 500 && error.response.data?.detail?.includes('Float types are not supported')) {
        throw new Error('Status check completed but had a response formatting issue. The comparison may still be processing.');
      }

      throw error;
    }
  },

  // List all comparisons for a workspace (fetches all pages automatically)
  async listComparisons(
    org_id: string,
    ws_id: string,
    limit: number = 100,
    status?: string
  ): Promise<ComparisonListResponse> {
    try {
      const allComparisons: ComparisonSummary[] = [];
      let nextToken: string | undefined = undefined;
      let hasMore = true;

      // Fetch all pages
      while (hasMore) {
        const params: Record<string, any> = {
          limit
        };

        if (status) {
          params.status = status;
        }

        if (nextToken) {
          params.next_token = nextToken;
        }

        const response = await axiosInstance.get(`/comparisons/org/${org_id}/ws/${ws_id}`, {
          params,
          timeout: 30000,
        });

        const data = sanitizeResponseData(response.data) as ComparisonListResponse;
        
        // Add comparisons from this page
        allComparisons.push(...data.comparisons);

        // Check if there are more pages
        hasMore = data.has_more && !!data.next_token;
        nextToken = data.next_token;

        console.log(`Fetched ${data.comparisons.length} comparisons, total so far: ${allComparisons.length}, has_more: ${hasMore}`);
      }

      // Return combined results
      return {
        comparisons: allComparisons,
        count: allComparisons.length,
        org_id,
        ws_id,
        has_more: false  // We fetched everything
      };
    } catch (error: any) {
      console.error('List comparisons error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to list comparisons');
    }
  },

  // Get detailed comparison information
  async getComparison(
    org_id: string,
    ws_id: string,
    comparison_id: string,
    include_results: boolean = true
  ): Promise<ComparisonDetail> {
    try {
      const response = await axiosInstance.get(
        `/comparisons/org/${org_id}/ws/${ws_id}/${comparison_id}`,
        {
          params: {
            include_results
          },
          timeout: 60000, // Longer timeout for fetching results
        }
      );

      return sanitizeResponseData(response.data) as ComparisonDetail;
    } catch (error: any) {
      console.error('Get comparison error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to get comparison');
    }
  },

  // Get download URL for comparison results
  async getComparisonDownloadUrl(
    org_id: string,
    ws_id: string,
    comparison_id: string,
    expiration: number = 7200
  ): Promise<{
    comparison_id: string;
    download_url: string;
    expires_in_seconds: number;
    file_size_mb?: string;
    filename: string;
  }> {
    try {
      const response = await axiosInstance.get(
        `/comparisons/org/${org_id}/ws/${ws_id}/${comparison_id}/download`,
        {
          params: {
            expiration
          },
          timeout: 30000,
        }
      );

      return sanitizeResponseData(response.data);
    } catch (error: any) {
      console.error('Get download URL error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to get download URL');
    }
  },

  // Delete a comparison
  async deleteComparison(
    org_id: string,
    ws_id: string,
    comparison_id: string
  ): Promise<{ comparison_id: string; deleted: boolean; s3_deleted: boolean }> {
    try {
      const response = await axiosInstance.delete(
        `/comparisons/org/${org_id}/ws/${ws_id}/${comparison_id}`,
        {
          timeout: 30000,
        }
      );

      return sanitizeResponseData(response.data);
    } catch (error: any) {
      console.error('Delete comparison error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to delete comparison');
    }
  }
};